import { useState } from 'react';
import { StarRating } from './StarRating';
import { useReplyToReview, useDeleteReviewReply } from '../hooks/useReviewReply';
import type { MyReview } from '../services/my-reviews.service';

const df = new Intl.DateTimeFormat('it-IT', {
  day: 'numeric', month: 'short', year: 'numeric',
});

export function MyReviewItem({ review }: { review: MyReview }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(review.reply?.text ?? '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const reply = useReplyToReview();
  const del = useDeleteReviewReply();

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    reply.mutate({ id: review.id, text: trimmed }, { onSuccess: () => setEditing(false) });
  };

  const confirmDelete = () => {
    del.mutate(review.id, { onSuccess: () => setConfirmingDelete(false) });
  };

  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-4">
      <div className="flex items-center justify-between">
        <span className="font-medium text-neutral-900">{review.authorName}</span>
        <span className="text-xs text-neutral-400">{df.format(new Date(review.createdAt))}</span>
      </div>
      <div className="mt-1"><StarRating rating={review.rating} size="sm" /></div>
      {review.feedback && <p className="mt-2 text-sm text-neutral-700">{review.feedback}</p>}

      {!review.canReply && (
        <p className="mt-3 text-xs text-neutral-400">Recensione senza testo — non è possibile rispondere.</p>
      )}

      {review.canReply && !review.reply && !editing && (
        <button
          onClick={() => { setText(''); setEditing(true); }}
          className="mt-3 rounded-md bg-brand-orange px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-orange/90"
        >
          Rispondi
        </button>
      )}

      {review.canReply && review.reply && !editing && (
        <div className="mt-3 rounded-lg border-l-2 border-brand-teal bg-neutral-50 px-3 py-2">
          <p className="text-xs font-semibold text-neutral-600">La tua risposta</p>
          <p className="mt-1 text-sm text-neutral-700">{review.reply.text}</p>
          <div className="mt-2 flex gap-3 text-xs">
            <button onClick={() => { setText(review.reply!.text); setEditing(true); }} className="font-medium text-brand-teal hover:underline">Modifica</button>
            {!confirmingDelete && (
              <button onClick={() => setConfirmingDelete(true)} className="font-medium text-red-600 hover:underline">Elimina</button>
            )}
            {confirmingDelete && (
              <>
                <button onClick={confirmDelete} disabled={del.isPending} className="font-medium text-red-600 hover:underline disabled:opacity-50">
                  {del.isPending ? 'Eliminazione…' : 'Conferma eliminazione'}
                </button>
                <button onClick={() => setConfirmingDelete(false)} disabled={del.isPending} className="font-medium text-neutral-500 hover:underline">Annulla</button>
              </>
            )}
          </div>
          {del.isError && <p className="mt-2 text-xs text-red-600">Errore durante l'eliminazione. Riprova.</p>}
        </div>
      )}

      {editing && (
        <div className="mt-3">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            maxLength={1000}
            rows={3}
            className="w-full rounded-md border border-neutral-200 p-2 text-sm text-neutral-900"
            placeholder="Scrivi una risposta pubblica…"
          />
          <div className="mt-2 flex gap-2">
            <button onClick={submit} disabled={reply.isPending || !text.trim()} className="rounded-md bg-brand-orange px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-orange/90 disabled:opacity-50">Pubblica</button>
            <button onClick={() => setEditing(false)} className="rounded-md px-3 py-1.5 text-sm text-neutral-500 hover:text-neutral-700">Annulla</button>
          </div>
          {reply.isError && <p className="mt-2 text-xs text-red-600">Errore durante la pubblicazione. Riprova.</p>}
        </div>
      )}
    </div>
  );
}
